// SPDX-License-Identifier: AGPL-3.0-or-later

import Updater from '@app/features/app/state/Updater';

const CONTROLLER_CHANGE_TIMEOUT_MS = 4000;

export async function ensureLatestAssets(options: {force?: boolean} = {}): Promise<{
	updateFound: boolean;
}> {
	await Updater.checkForUpdates(options.force ?? false);
	return {updateFound: Updater.updateInfo.web.available};
}

/** Activation is bounded so offline or stalled worker updates cannot trap the reload button. */
export async function activateLatestServiceWorker(): Promise<void> {
	if (!('serviceWorker' in navigator)) return;
	const workers = navigator.serviceWorker;
	await new Promise<void>((resolve) => {
		let settled = false;
		let installing: ServiceWorker | null = null;
		const finish = () => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timeout);
			workers.removeEventListener('controllerchange', finish);
			installing?.removeEventListener('statechange', onStateChange);
			resolve();
		};
		const onStateChange = () => {
			if (installing?.state === 'installed') installing.postMessage({type: 'SKIP_WAITING'});
			if (installing?.state === 'activated' || installing?.state === 'redundant') finish();
		};
		const timeout = window.setTimeout(finish, CONTROLLER_CHANGE_TIMEOUT_MS);
		workers.addEventListener('controllerchange', finish);
		void (async () => {
			try {
				const registration = await workers.getRegistration();
				if (settled) return;
				if (!registration) return finish();
				await registration.update();
				if (settled) return;
				if (registration.waiting) {
					registration.waiting.postMessage({type: 'SKIP_WAITING'});
				} else if (registration.installing) {
					installing = registration.installing;
					installing.addEventListener('statechange', onStateChange);
					onStateChange();
				} else {
					finish();
				}
			} catch (error) {
				console.warn('[Versioning] Failed to activate latest service worker', error);
				finish();
			}
		})();
	});
}
